import React from 'react';
import { Link } from 'react-router-dom';
import platformImage from '../assests/main image.png';

const cloudCapabilities = [
  'Business messaging', 'Template messages', 'Customer notifications', 'Authentication',
  'Marketing communication', 'Media messaging', 'Customer support workflows',
  'Automated communication', 'Application integrations', 'Webhook-based events',
];

const messageTypes = ['Text messages', 'Images', 'Videos', 'Documents', 'Audio', 'Message templates'];
const integrationTypes = ['CRM platforms', 'Ecommerce applications', 'SaaS platforms', 'Websites', 'Mobile applications', 'ERP systems', 'Booking systems', 'Customer support software', 'Internal business applications', 'Custom APIs'];
const templateUseCases = [
  ['Order Confirmation', 'Confirm a customer\'s purchase.'],
  ['Shipping Update', 'Inform customers when an order is shipped.'],
  ['Appointment Reminder', 'Remind customers about upcoming appointments.'],
  ['Authentication', 'Send permitted authentication and verification messages.'],
  ['Marketing', 'Communicate permitted promotions and offers.'],
  ['Service Updates', 'Send important updates related to products or services.'],
];
const developerTools = [
  ['API Messaging', 'Send WhatsApp messages from your backend application.'],
  ['Templates', 'Use approved WhatsApp templates for supported business communication.'],
  ['Media', 'Build messaging workflows around images, videos, documents, and audio.'],
  ['Webhooks', 'Receive messaging events and connect WhatsApp activity to application logic.'],
  ['Automation', 'Trigger WhatsApp communication when application events occur.'],
  ['Customer Management', 'Connect customer records with WhatsApp communication workflows.'],
];
const automationUseCases = ['Customer onboarding', 'Order notifications', 'Appointment reminders', 'Payment updates', 'Lead follow-ups', 'Service notifications', 'Customer feedback', 'Marketing campaigns', 'Account notifications'];
const customerNotifications = [
  ['Order Updates', 'Send order confirmations and status updates.'],
  ['Appointment Updates', 'Send appointment confirmations and reminders.'],
  ['Payment Updates', 'Send permitted payment-related notifications.'],
  ['Account Updates', 'Communicate important account information.'],
  ['Service Updates', 'Keep customers informed about changes and important events.'],
];
const marketingCampaigns = ['Product launches', 'Special offers', 'Seasonal promotions', 'New services', 'Events', 'Customer re-engagement', 'Product announcements'];
const crmFields = ['Customers', 'Leads', 'Tags', 'Customer status', 'Appointments', 'Feedback', 'Customer communication'];
const mediaTypes = ['Images', 'Videos', 'Documents', 'Audio'];
const mediaExamples = [
  ['Ecommerce', 'Send product information or documents.'],
  ['Real Estate', 'Share property-related media.'],
  ['Education', 'Share permitted documents and information.'],
  ['Service Businesses', 'Send relevant files and customer information.'],
];
const platformBenefits = [
  ['Developer-Friendly APIs', 'Connect your applications with WhatsApp communication.'],
  ['Template Management', 'Organize and manage WhatsApp message templates.'],
  ['Automation', 'Build event-driven and scheduled messaging workflows.'],
  ['Campaigns', 'Manage customer communication campaigns.'],
  ['CRM', 'Organize customer and lead information.'],
  ['Webhooks', 'Connect messaging events to your applications.'],
  ['Media Messaging', 'Build workflows around supported WhatsApp media types.'],
  ['Centralized Management', 'Manage important communication workflows from one platform.'],
];
const setupSteps = [
  ['Connect Your WhatsApp Business Setup', 'Configure the WhatsApp Business infrastructure required for your integration.'],
  ['Configure HAWKTRION', 'Set up your business, messaging configuration, and integration requirements.'],
  ['Create Templates', 'Create the message templates required for your business communication.'],
  ['Connect Your Application', 'Integrate your website, CRM, SaaS platform, or custom application with the API.'],
  ['Configure Webhooks', 'Connect webhook events with your backend application.'],
  ['Automate Communication', 'Trigger WhatsApp messages based on customer actions and business events.'],
  ['Monitor Communication', 'Manage messaging workflows and customer communication through the platform.'],
];
const industryUseCases = [
  ['Ecommerce', 'Automate order confirmations, shipping updates, delivery notifications, and customer communication.'],
  ['SaaS', 'Integrate WhatsApp into your SaaS application workflows.'],
  ['Education', 'Send permitted notifications, reminders, and announcements.'],
  ['Real Estate', 'Follow up with leads and communicate appointment information.'],
  ['Hospitality', 'Send booking-related communication, reminders, and service updates.'],
  ['Professional Services', 'Automate customer notifications and appointment communication.'],
  ['Agencies', 'Create WhatsApp solutions for clients.'],
  ['Customer Support', 'Connect customer conversations with support workflows.'],
];
const comparisonRows = [
  ['Application integration', 'Limited', 'Yes'],
  ['Automated workflows', 'Limited', 'Yes'],
  ['Webhooks', 'No', 'Yes'],
  ['Programmatic messaging', 'No', 'Yes'],
  ['Business event triggers', 'Limited', 'Yes'],
  ['CRM integration', 'Limited', 'Yes'],
  ['Scheduled workflows', 'Limited', 'Yes'],
  ['Developer integration', 'No', 'Yes'],
];
const faqs = [
  ['What is WhatsApp Cloud API?', 'WhatsApp Cloud API is a cloud-hosted API provided by Meta that allows businesses and developers to integrate WhatsApp messaging into applications and business workflows.'],
  ['What can I do with WhatsApp Cloud API?', 'You can build supported messaging, notification, authentication, marketing, customer-service, and automation workflows, subject to applicable WhatsApp policies and requirements.'],
  ['Is WhatsApp Cloud API suitable for developers?', 'Yes. Developers can integrate WhatsApp communication with websites, SaaS applications, CRM systems, and custom backend applications.'],
  ['Can I connect WhatsApp Cloud API to my CRM?', 'Yes. API and webhook integrations can connect WhatsApp communication with CRM and customer-management workflows.'],
  ['Can I send images and documents?', 'WhatsApp Cloud API supports several message and media types. Your implementation should follow applicable API requirements and supported formats.'],
  ['Can I automate WhatsApp messages?', 'Yes. Businesses can create automated workflows using APIs, templates, application events, and other supported mechanisms.'],
  ['What are WhatsApp webhooks?', 'Webhooks allow applications to receive supported WhatsApp events and process them programmatically.'],
  ['Can I use WhatsApp Cloud API for SaaS?', 'Yes. SaaS platforms can integrate WhatsApp communication into their application workflows and customer experiences.'],
  ['Can I use WhatsApp Cloud API for marketing?', 'Businesses can use WhatsApp for permitted marketing communication, subject to applicable WhatsApp policies, customer consent, and template requirements.'],
  ['Do I need WhatsApp message templates?', 'Template requirements depend on the type and timing of business-initiated communication. Outside the applicable customer-service window, business-initiated messages require approved templates.'],
  ['Is WhatsApp Cloud API free?', 'API infrastructure and messaging costs depend on applicable Meta pricing and your chosen platform or provider setup. Check current WhatsApp Business Platform pricing before estimating communication costs.'],
];


const BulletList = ({ items }) => (
  <ul className="business-api-list">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="business-api-heading">
    {eyebrow && <div className="sec-label"><span className="sec-label-dot"></span>{eyebrow}</div>}
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

const CardGrid = ({ items }) => (
  <div className="business-api-grid">
    {items.map(([title, description]) => (
      <article className="business-api-card" key={title}>
        <h3>{title}</h3>
        <p>{description}</p>
      </article>
    ))}
  </div>
);

const Workflow = ({ steps }) => (
  <ol className="business-api-workflow">
    {steps.map((step, index) => (
      <li key={step}>
        <span className="business-api-step-number">{index + 1}</span>
        <span>{step}</span>
      </li>
    ))}
  </ol>
);

const WhatsappCloudApiContent = () => (
  <div className="business-api-content cloud-api-content">
    <section className="business-api-section business-api-intro">
      <div className="container">
        <div className="business-api-intro-grid">
          <div className="business-api-intro-copy">
            <p className="business-api-lead">Connect WhatsApp Cloud API with your business applications using HAWKTRION.</p>
            <p>Send WhatsApp messages, manage templates, deliver media, automate customer communication, receive webhook events, and connect WhatsApp with your CRM, SaaS platform, or custom application.</p>
            <div className="business-api-actions">
              <Link to="/signup" className="btn btn-p">Get Started</Link>
              <Link to="/whatsapp-api-documentation/" className="btn btn-s">Explore API Documentation</Link>
            </div>
          </div>
          <figure className="business-api-visual">
            <img src={platformImage} alt="HAWKTRION WhatsApp Business platform overview with messaging, CRM, automation, campaigns, integrations, and analytics" />
            <figcaption>Connect WhatsApp Cloud API to customer messaging, automation, and business systems.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Cloud API Overview" title="Build Powerful WhatsApp Experiences With Cloud API">
          WhatsApp Cloud API provides businesses and developers with an API-based way to integrate WhatsApp communication into applications and business workflows. HAWKTRION provides a centralized platform for messaging, templates, campaigns, automation, CRM workflows, and developer APIs.
        </SectionHeading>
        <p className="business-api-copy">Instead of treating WhatsApp as a separate communication channel, connect it directly with your applications.</p>
        <h3 className="business-api-subheading">Connect Your Application to WhatsApp</h3>
        <Workflow steps={['Your application', 'HAWKTRION', 'WhatsApp Cloud API', 'Customer']} />
        <p className="business-api-copy">Create automated communication workflows based on customer actions and business events.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="The Platform" title="What Is WhatsApp Cloud API?">
          WhatsApp Cloud API is Meta&apos;s cloud-hosted API solution for businesses and developers that need to integrate WhatsApp messaging into their applications. It enables programmatic communication instead of relying only on manual messaging through the WhatsApp application.
        </SectionHeading>
        <p className="business-api-copy">With HAWKTRION, build a management and integration layer around your WhatsApp communication workflows.</p>
        <h3 className="business-api-subheading">Cloud API Capabilities</h3>
        <BulletList items={cloudCapabilities} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Messaging" title="WhatsApp Cloud API Features" />
        <div className="business-api-feature-grid">
          <article className="business-api-card">
            <h3>Send WhatsApp Messages</h3>
            <p>Build messaging workflows that allow your application to communicate with customers through WhatsApp.</p>
            <BulletList items={messageTypes} />
            <p>Create communication workflows that fit your business requirements.</p>
          </article>
          <article className="business-api-card">
            <h3>WhatsApp Message Templates</h3>
            <p>Templates are important for business-initiated WhatsApp communication. Create and manage templates for customer communication scenarios.</p>
            <div className="business-api-event-list">
              {templateUseCases.map(([title, description]) => <p key={title}><strong>{title}</strong><span>{description}</span></p>)}
            </div>
            <p>Outside the applicable customer-service window, WhatsApp requires approved templates for business-initiated conversations. Template use is subject to WhatsApp policies and requirements.</p>
          </article>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Connect Your Stack" title="WhatsApp Cloud API Integration">
          Your business probably already uses CRM, ecommerce, ERP, booking, or SaaS software. HAWKTRION helps connect those systems with WhatsApp communication.
        </SectionHeading>
        <h3 className="business-api-subheading">Integrate With</h3>
        <BulletList items={integrationTypes} />
        <h3 className="business-api-subheading">Example Integration</h3>
        <Workflow steps={['Customer places order', 'Ecommerce application', 'HAWKTRION API', 'WhatsApp Cloud API', 'Order confirmation', 'Customer']} />
        <p className="business-api-copy">Your application can automatically communicate with customers when business events occur.</p>
        <div className="business-api-example">
          <div className="business-api-example-event">
            <span className="business-api-example-label">ECOMMERCE EVENT</span>
            <strong>order.created</strong>
            <p>New order is ready for confirmation.</p>
          </div>
          <div className="business-api-example-arrow" aria-hidden="true">→</div>
          <div className="business-api-example-chat">
            <div className="business-api-example-chat-head">
              <span className="business-api-chat-avatar">H</span>
              <span><strong>HAWKTRION Store</strong><small>WhatsApp Business</small></span>
              <span className="business-api-chat-status">Delivered</span>
            </div>
            <p className="business-api-chat-bubble">Thanks for your order! We&apos;re preparing it now and will send another update when it ships.</p>
            <p className="business-api-chat-meta">Template: order_confirmation · just now</p>
          </div>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For Developers" title="Build WhatsApp Into Your Application">
          HAWKTRION provides developer-focused API capabilities for businesses building custom WhatsApp integrations.
        </SectionHeading>
        <CardGrid items={developerTools} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Events" title="WhatsApp Cloud API Webhooks">
          Webhooks allow your application to receive events from WhatsApp and respond to customer communication programmatically.
        </SectionHeading>
        <BulletList items={['Incoming customer messages', 'Message status updates', 'Delivery status', 'Read status', 'Other supported WhatsApp events']} />
        <h3 className="business-api-subheading">Example Webhook Flow</h3>
        <Workflow steps={['Customer sends message', 'WhatsApp', 'Webhook', 'HAWKTRION', 'Your application', 'Business logic']} />
        <p className="business-api-copy">Create responsive WhatsApp applications and automated customer workflows.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Automation" title="WhatsApp Cloud API Automation">
          Connect WhatsApp Cloud API with business processes and automate repetitive communication.
        </SectionHeading>
        <BulletList items={automationUseCases} />
        <h3 className="business-api-subheading">Event-Based Automation</h3>
        <Workflow steps={['Business event', 'Application', 'HAWKTRION', 'WhatsApp Cloud API', 'Customer message']} />
        <p className="business-api-copy">Automation helps reduce manual communication and create consistent customer experiences.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For SaaS Platforms" title="Add WhatsApp Communication to Your SaaS Product">
          Integrate WhatsApp communication directly into your product workflows.
        </SectionHeading>
        <BulletList items={['Customer notifications', 'Appointment management', 'Transaction updates', 'Authentication', 'Customer support', 'Marketing', 'Automated reminders', 'Business alerts']} />
        <p className="business-api-copy">Instead of asking customers to manage WhatsApp separately, integrate communication into your existing SaaS experience.</p>
        <Workflow steps={['SaaS application', 'HAWKTRION API', 'WhatsApp Cloud API', 'SaaS customer']} />
        <Link to="/whatsapp-api-for-saas/" className="business-api-text-link">Build WhatsApp Into Your SaaS</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="For Agencies" title="Create WhatsApp Solutions for Your Clients">
          Digital agencies can use HAWKTRION to create WhatsApp communication solutions for different businesses.
        </SectionHeading>
        <BulletList items={['Ecommerce businesses', 'Service businesses', 'Education', 'Real estate', 'Hospitality', 'Professional services', 'Retail', 'SaaS businesses']} />
        <p className="business-api-copy">Create reusable communication workflows and customize them for individual client requirements.</p>
        <Link to="/whatsapp-api-for-agencies/" className="business-api-text-link">Build WhatsApp Solutions for Clients</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Updates" title="WhatsApp Cloud API for Customer Notifications" />
        <CardGrid items={customerNotifications} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Campaigns" title="Engage Customers Through WhatsApp">
          WhatsApp can be an important channel for customer engagement and permitted marketing communication. Use HAWKTRION to organize campaigns and customer audiences.
        </SectionHeading>
        <BulletList items={marketingCampaigns} />
        <p className="business-api-copy">Use appropriate customer consent and comply with applicable WhatsApp policies when sending marketing communication.</p>
        <Link to="/whatsapp-marketing/" className="business-api-text-link">Explore WhatsApp Marketing</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Management" title="WhatsApp Cloud API CRM">
          Connect customer data with WhatsApp. HAWKTRION provides CRM-oriented workflows for managing customer information and communication.
        </SectionHeading>
        <BulletList items={crmFields} />
        <h3 className="business-api-subheading">Example Customer Journey</h3>
        <Workflow steps={['New lead', 'WhatsApp communication', 'Lead qualification', 'Appointment', 'Customer', 'Follow-up', 'Feedback']} />
        <p className="business-api-copy">Create structured customer journeys around WhatsApp communication.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Rich Communication" title="WhatsApp Cloud API Media Messaging">
          Business communication often requires more than text. Build workflows around supported media types.
        </SectionHeading>
        <BulletList items={mediaTypes} />
        <CardGrid items={mediaExamples} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Campaign Operations" title="Manage WhatsApp Campaign Communication">
          HAWKTRION can help you manage campaign workflows, audience selection, and scheduled communication.
        </SectionHeading>
        <Workflow steps={['Customer database', 'Audience selection', 'Campaign', 'Approved template', 'WhatsApp Cloud API', 'Customer']} />
        <p className="business-api-copy">Build structured WhatsApp marketing workflows while following applicable messaging policies.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Why HAWKTRION" title="One Platform for WhatsApp Communication">
          HAWKTRION combines API integration with business messaging tools.
        </SectionHeading>
        <CardGrid items={platformBenefits} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Getting Started" title="How WhatsApp Cloud API Works With HAWKTRION" />
        <ol className="business-api-steps">
          {setupSteps.map(([title, description], index) => (
            <li className="business-api-step" key={title}>
              <span className="business-api-step-number">{index + 1}</span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Use Cases" title="WhatsApp Cloud API Use Cases" />
        <CardGrid items={industryUseCases} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Compare Workflows" title="WhatsApp Cloud API vs Manual Messaging">
          Manual messaging can become difficult when a business needs to manage a high volume of customer interactions. API-based communication connects messaging with software and automated workflows.
        </SectionHeading>
        <div className="cloud-api-table-wrap">
          <table className="cloud-api-table">
            <caption>Capabilities of manual messaging and Cloud API integration</caption>
            <thead><tr><th scope="col">Capability</th><th scope="col">Manual Messaging</th><th scope="col">Cloud API Integration</th></tr></thead>
            <tbody>{comparisonRows.map(([capability, manual, cloud]) => <tr key={capability}><th scope="row">{capability}</th><td>{manual}</td><td>{cloud}</td></tr>)}</tbody>
          </table>
        </div>
        <p className="business-api-copy">HAWKTRION adds management, automation, and integration capabilities around your WhatsApp communication infrastructure.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Scale Your Workflows" title="Build Scalable WhatsApp Communication">
          Whether you&apos;re building a SaaS application, operating an ecommerce business, managing customer support, or running a digital agency, WhatsApp Cloud API can become an important part of your communication infrastructure.
        </SectionHeading>
        <p className="business-api-copy">HAWKTRION brings WhatsApp Cloud API, APIs, webhooks, templates, automation, CRM, and campaigns into a connected business communication workflow.</p>
      </div>
    </section>

    <section className="business-api-section business-api-faq" id="cloud-api-faq">
      <div className="container">
        <SectionHeading eyebrow="Answers" title="WhatsApp Cloud API FAQs" />
        <div className="business-api-faq-list">
          {faqs.map(([question, answer]) => (
            <details key={question}><summary>{question}</summary><p>{answer}</p></details>
          ))}
        </div>
      </div>
    </section>

    <section className="business-api-section business-api-final-cta">
      <div className="container">
        <div className="business-api-cta-panel">
          <SectionHeading eyebrow="Get Started" title="Connect Your Applications With WhatsApp">
            Build customer communication workflows with WhatsApp Cloud API and HAWKTRION. Connect applications, automate business communication, manage templates, and create better customer experiences.
          </SectionHeading>
          <div className="business-api-actions">
            <Link to="/signup" className="btn btn-p">Get Started With HAWKTRION</Link>
            <Link to="/whatsapp-api-documentation/" className="btn btn-s">View API Documentation</Link>
          </div>
        </div>
      
      </div>
    </section>
  </div>
);

export default WhatsappCloudApiContent;